/**
 * Tooth Taxi Preset — option catalog for the "ตรวจสุขภาพ Tooth taxi" document.
 *
 * Used by the Tooth Taxi Preset picker (DocumentGenerator → ToothTaxiPresetDialog),
 * shown only for Tooth Taxi treatments (branch_id === 9). The dentist ticks the
 * relevant Chief Complaint / Diagnosis / Treatment items; selections become the
 * {{chief_complaint}}, {{diagnosis}} and {{treatment}} placeholders in the PDF.
 *
 * Edit freely — add / rename / reorder items here without touching dialog logic.
 *
 * Option shape:
 *   { label }                  — plain checkbox, no typed input
 *   { label, on: true }        — reveals an "on …" text box (usually tooth numbers)
 *   { label, with: true }      — reveals a "with …" text box
 *   { label, other: true }     — free-text only; the typed text IS the line
 *   extra: [{ key, label }]    — additional sub-inputs, e.g. Filling's Base / Subbase
 */

export const TOOTH_TAXI_BRANCH_ID = 9

// Chief Complaint — simple multi-select. "อื่นๆ" is a separate free-text field
// handled by the dialog (see ToothTaxiPresetDialog).
export const CHIEF_COMPLAINTS = [
  'ตรวจสุขภาพช่องปาก',
  'แปรงฟันแล้วมีเลือดออก',
  'ต้องการขูดหินปูน',
  'รู้สึกมีฟันผุ',
  'เสียวฟัน',
  'ปวดฟัน',
  'ฟันโยก',
]

export const DIAGNOSIS_OPTIONS = [
  { label: 'Gingivitis' },
  { label: 'Generalized Chronic Periodontitis' },
  { label: 'Localized Chronic Periodontitis', on: true },
  { label: 'Deep Pit & Fissure', on: true },
  { label: 'Dental Caries', on: true },
  { label: 'Cervical Abrasion (NCCL)', on: true },
  { label: 'Reversible Pulpitis', on: true },
  { label: 'Irreversible Pulpitis', on: true },
  { label: 'Pulp Necrosis', on: true },
  { label: 'Retained root (RR)', on: true },
  { label: 'Periodontally Hopeless Tooth', on: true },
  { label: 'Impaction / Embedded Tooth', on: true },
  { label: 'Prolonged Retention', on: true },
  { label: 'Other', other: true },
]

export const TREATMENT_OPTIONS = [
  { label: 'Oral Examination & Consultation' },
  { label: 'X-ray (Pa / BW)', on: true },
  { label: 'Scaling FM., Polishing, OHI' },
  { label: 'Airflow' },
  { label: 'Polishing & Fluoride application', with: true },
  { label: 'Sealant', on: true },
  {
    label: 'Filling w/ Composite Resin',
    on: true,
    extra: [
      { key: 'base', label: 'Base w/' },
      { key: 'subbase', label: 'Subbase w/' },
    ],
  },
  { label: 'Topical Anesthesia', on: true },
  { label: 'Local Anesthesia injection', with: true },
  { label: 'Simple Extraction', on: true },
  { label: 'Surgical Extraction', on: true },
  { label: 'Suture', with: true },
  { label: 'Other', other: true },
]

/**
 * Render one selected option + its captured inputs into a single display line.
 * Returns null when an "other"/free-text item has no text (→ skip the line).
 *
 *   { label:'Irreversible Pulpitis', on:true } + { input:'36' }  → "Irreversible Pulpitis on 36"
 *   { label:'Filling…', extra:[base,subbase] } + { input:'14', base:'GI' }
 *                                                → "Filling w/ Composite Resin on 14 (Base w/ GI)"
 *   { label:'Other', other:true } + { text:'Abfraction 25' }     → "Abfraction 25"
 */
export function formatPresetLine(opt, state = {}) {
  if (opt.other) {
    const t = String(state.text || '').trim()
    return t || null
  }
  let line = opt.label
  const main = String(state.input || '').trim()
  if (opt.on && main) line += ` on ${main}`
  else if (opt.with && main) line += ` with ${main}`

  if (opt.extra && opt.extra.length) {
    const parts = opt.extra
      .map((e) => {
        const v = String(state[e.key] || '').trim()
        return v ? `${e.label} ${v}` : null
      })
      .filter(Boolean)
    if (parts.length) line += ` (${parts.join(' / ')})`
  }
  return line
}
